import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { KIToolsUser, SelectableUserlistOfBot } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { FrontendUrlsService } from '@shared-angular/services/frontend-urls.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from './route-share.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ToastrService } from '@shared-angular/services/toastr.service';


@Injectable({
  providedIn: 'root'
})
export class AccesscontrolService {
  // Signal-backed, weil per Socket-Event/nach await geaendert und in OnPush-Templates gelesen; Updates immutabel.
  private readonly _kiToolUserList = signal<KIToolsUser[]>([]);
  public get kiToolUserList(): KIToolsUser[] { return this._kiToolUserList(); }
  public set kiToolUserList(value: KIToolsUser[]) { this._kiToolUserList.set(value); }
  private readonly _selectableUserlistOfBots = signal<SelectableUserlistOfBot[]>([]);
  public get selectableUserlistOfBots(): SelectableUserlistOfBot[] { return this._selectableUserlistOfBots(); }
  public set selectableUserlistOfBots(value: SelectableUserlistOfBot[]) { this._selectableUserlistOfBots.set(value); }
  public filterBotByName: string = "";
  private guestIdCounter: number = 1;
  private readonly frontendUrls = inject(FrontendUrlsService);

  private _searchString: string = "";
  private readonly _filteredUsers = signal<KIToolsUser[]>([]);

  private readonly _selectedUserIds = signal<string[]>([]);
  public get selectedUserIds(): string[] { return this._selectedUserIds(); }
  public set selectedUserIds(value: string[]) { this._selectedUserIds.set(value); }
  private readonly _allSelected = signal(false);
  public get allSelected(): boolean { return this._allSelected(); }
  public set allSelected(value: boolean) { this._allSelected.set(value); }

  constructor(
    private http: HttpClient,
    private rss: RouteShareService,
    private toastr: ToastrService,
    protected socketAdminService: SocketAdminService,
    private languageService: LanguageService
  ) {

    socketAdminService.onUserListItemUpdate.subscribe((d: KIToolsUser) => {
      if (this.rss.botConfig._id != d.botId) {
        return;
      }
      const exists = this.kiToolUserList.some(u => u.guestId == d.guestId);
      this.kiToolUserList = exists
        ? this.kiToolUserList.map(u => u.guestId == d.guestId
          ? { ...u, accessToken: d.accessToken, guestId: d.guestId, metadata: d.metadata, stats: d.stats, created: d.created, updated: d.updated }
          : u)
        : [...this.kiToolUserList, d];
      this.filterUpdate();
    });


    socketAdminService.onUserListItemDelete.subscribe((d: { botId: string, userListItemId: string }) => {
      if (this.rss.botConfig._id != d.botId) {
        return;
      }
      this.kiToolUserList = this.kiToolUserList.filter(u => u._id != d.userListItemId);
      this.selectedUserIds = this.selectedUserIds.filter(id => id != d.userListItemId);
      this.filterUpdate();
    });
  }



  public async deleteSelected() {
    await Promise.all(this.selectedUserIds.map(user => this.deleteUserInList(user)));
    this.allSelected = false;
    this.selectedUserIds = [];
  }

  public get filteredUsers() {
    return this._filteredUsers();
  }

  public get exportingUsers() {
    return this._filteredUsers().map(u => {
      const accessLink = this.frontendUrls.bot('/' + this.languageService.getLanguage() + "/botaccess?accesstoken=" + u.accessToken);
      return {
        guestId: u.guestId,
        metadata: u.metadata,
        created: u.created,
        updated: u.updated,
        chat: accessLink,
        audio: accessLink + "&target=audio",
        "avatar-nova": accessLink + "&target=avatar-nova",
        "avatar-vega": accessLink + "&target=avatar-vega"
      };
    });
  }

  public get exportingStats() {
    return this._filteredUsers().map(u => {
      return {
        guestId: u.guestId,
        chat: u.stats.chat.length,
        audio: u.stats.audio.length,
        avatar: u.stats.avatar.length
      };
    });
  }

  public get searchString(): string {
    return this._searchString;
  }

  public set searchString(str: string) {
    this._searchString = str;
    this.filterUpdate();

  }
  protected filterUpdate() {
    this._filteredUsers.set(this.kiToolUserList.filter(g => RegExp(this._searchString.toLocaleLowerCase()).exec(g.guestId.toLowerCase())).reverse());
  }

  public get filteredselectableUserlistOfBots() {
    return this.selectableUserlistOfBots.filter(uol => uol.botId != this.rss.botConfig._id && uol.botName.toLowerCase().includes(this.filterBotByName.toLowerCase())).filter((_uol, index) => index < 21);
  }

  public getUserUsageCount(guestId: string) {
    return this.selectableUserlistOfBots.filter((ul) => ul.users.find(u => u.guestId == guestId)).length;
  }
  public async loadSelectableUsers(): Promise<void> {
    const result = await firstValueFrom(this.http.get<SelectableUserlistOfBot[]>("/api/toolbox/access/selectableusers/" + this.rss.botConfig._id));
    this.selectableUserlistOfBots = result;
  }


  public async loadUserList(): Promise<void> {
    const result = await this.getUserList(this.rss.botConfig._id);
    this.kiToolUserList = result;
    this.filterUpdate();
  }

  private async getUserList(botId: string): Promise<KIToolsUser[]> {
    const result = await firstValueFrom(this.http.get<KIToolsUser[]>("/api/toolbox/access/userlist/" + botId));
    return result;
  }

  public async addUserToList(guestId: string = "", metadata: string = ""): Promise<KIToolsUser> {

    if (!guestId) {
      do {
        guestId = `Freigabe-${(this.guestIdCounter++).toString()}`;
      } while (this.kiToolUserList.some(user => user.guestId === guestId));
    }

    const userToAdd: Omit<KIToolsUser, 'stats'> = {
      _id: "",
      guestId: guestId,
      accessToken: "",
      metadata: metadata,
      botId: this.rss.botConfig._id,
      created: "",
      updated: ""
    };

    const createdUser = await firstValueFrom(this.http.post<KIToolsUser>("/api/toolbox/access/addusertolist/" + this.rss.botConfig._id, { user: userToAdd }));
    await this.loadSelectableUsers();
    this.filterUpdate();
    this.toastr.success("SAVED");
    return createdUser;
  }

  public async resetUserStats(userId: string) {
    const userIndex = this.kiToolUserList.findIndex(ul => ul._id == userId);
    if (userIndex >= 0) {
      const updatedUser = await firstValueFrom(this.http.put<KIToolsUser>("/api/toolbox/access/resetstats/" + this.rss.botConfig._id + "/" + userId, { user: this.kiToolUserList[userIndex] }));
      this.kiToolUserList = this.kiToolUserList.map(u => u._id == userId ? updatedUser : u);
      this.toastr.success("SAVED");
      return updatedUser;
    }
    throw new Error(`Userid ${userId} not found for update`);
  }

  public async updateUserInList(user: KIToolsUser): Promise<KIToolsUser> {

    const userIndex = this.kiToolUserList.findIndex(ul => ul._id == user._id);
    if (userIndex >= 0) {
      if (this.kiToolUserList.filter(u => u.guestId == this.kiToolUserList[userIndex]?.guestId).length > 1) {
        this.toastr.error("SAVED", $localize`:@@disallowed-duplicate-user:Dieser Nutzer wurde bereits hinzugefügt.`);
        throw new Error(`Userid ${user._id} cannot exists multiple times`);
      }
      const updatedUser = await firstValueFrom(this.http.put<KIToolsUser>("/api/toolbox/access/updateuserinlist/" + this.rss.botConfig._id + "/" + user._id, { user }));
      this.kiToolUserList = this.kiToolUserList.map(u => u._id == user._id ? updatedUser : u);
      this.toastr.success("SAVED");
      await this.loadSelectableUsers();

      return updatedUser;
    }
    throw new Error(`Userid ${user._id} not found for update`);
  }

  public async deleteUserInList(userId: string): Promise<void> {
    await firstValueFrom(this.http.delete("/api/toolbox/access/removeuserfromlist/" + this.rss.botConfig._id + "/" + userId));
    this.toastr.success("SAVED");
  }
}
